/**
 * FactorListItemAtom.tsx
 *
 * A list item component for the status factors sidebar.
 * Displays factor name, type icon, status badge, and dirty indicator.
 */

import React from 'react';
import { useTranslation } from 'localization';
import type { StatusFactorType, StatusValue } from 'types/spherePoints';
import type { FactorStatus } from '../../../types/statusFactors';
import StatusBadgeAtom from '../status-badge/StatusBadgeAtom';
import DirtyIndicatorAtom from '../DirtyIndicator/DirtyIndicatorAtom';
import './FactorListItemAtom.scss';

export interface FactorListItemAtomProps {
  /** Factor ID */
  id: string;
  /** Factor display name */
  name: string;
  /** Factor type */
  factorType: StatusFactorType | null;
  /** Current factor status */
  status: FactorStatus;
  /** Whether this item is selected */
  selected?: boolean;
  /** Whether the factor has unsaved changes */
  isDirty?: boolean;
  /** Whether the factor has validation errors */
  hasErrors?: boolean;
  /** Click handler */
  onClick?: (id: string) => void;
  /** Additional CSS class names */
  className?: string;
  /** Test ID for automation */
  'data-testid'?: string;
}

const LIFECYCLE_STATUSES = new Set<FactorStatus>(['deactivated', 'paused', 'observing', 'draft']);

function isLifecycleStatus(s: FactorStatus): s is 'deactivated' | 'paused' | 'observing' | 'draft' {
  return LIFECYCLE_STATUSES.has(s);
}

/**
 * Board 1 factor-type icon — a PrimeIcon class matching the design handoff's
 * meaningful glyphs (pencil for manual, sitemap for related spheres, chart-bar
 * for arc health) instead of a single-letter tile.
 */
const getFactorTypeIcon = (factorType: StatusFactorType | null): string => {
  switch (factorType) {
    case 'manual':
      return 'pi pi-pencil';
    case 'related_spheres':
      return 'pi pi-sitemap';
    case 'arc_health':
      return 'pi pi-chart-bar';
    default:
      return 'pi pi-question-circle';
  }
};

/**
 * Board 1 subtitle line — "Manual · at risk" beneath the factor name in the
 * rail. Uses the localized factor-type short name plus the current status.
 */
const getFactorTypeShortName = (
  factorType: StatusFactorType | null,
  t: (key: string, defaultValue: string) => string,
): string => {
  switch (factorType) {
    case 'manual':
      return t('statusFactors.types.manual.short', 'Manual');
    case 'related_spheres':
      return t('statusFactors.types.relatedSpheres.short', 'Related spheres');
    case 'arc_health':
      return t('statusFactors.types.arcHealth.short', 'Arc health');
    default:
      return t('statusFactors.types.unknown.short', 'Unknown');
  }
};

const getStatusShortLabel = (
  status: FactorStatus,
  t: (key: string) => string,
): string => {
  if (status === 'deactivated' || status === 'paused' || status === 'observing' || status === 'draft') {
    return t(`statusFactors.mode.${status}`);
  }
  return t(`statusVocabulary.${status}`);
};

const FactorListItemAtom: React.FC<FactorListItemAtomProps> = ({
  id,
  name,
  factorType,
  status,
  selected = false,
  isDirty = false,
  hasErrors = false,
  onClick,
  className,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();

  const handleClick = () => {
    onClick?.(id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(id);
    }
  };

  // Display name: use provided name or factor type label
  const displayName = name || getDefaultFactorName(factorType, t);

  const isDeactivated = status === 'deactivated';
  const isPaused = status === 'paused';

  const classes = [
    'factor-list-item',
    selected && 'factor-list-item--selected',
    isDirty && 'factor-list-item--dirty',
    hasErrors && 'factor-list-item--has-errors',
    isDeactivated && 'factor-list-item--deactivated',
    isPaused && 'factor-list-item--paused',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      aria-selected={selected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={testId}
    >
      {/* Type icon — PrimeIcon glyph per Board 1 (was single-letter tile).
          The icon wrapper is the tinted rounded square; the <i> is centered
          inside it, not styled as its own tile. */}
      <div
        className="factor-list-item__icon factor-list-item__icon-badge"
        title={getFactorTypeTooltip(factorType, t)}
      >
        <i className={`${getFactorTypeIcon(factorType)} factor-list-item__icon-glyph`} aria-hidden="true" />
      </div>

      {/* Name + subtitle (Board 1: "Manual · at risk" beneath the name). */}
      <div className="factor-list-item__content">
        <span className="factor-list-item__name">{displayName}</span>
        <span className="factor-list-item__subtitle">
          {getFactorTypeShortName(factorType, t)}
          <span aria-hidden="true"> · </span>
          {getStatusShortLabel(status, t)}
        </span>
        {hasErrors && (
          <span className="factor-list-item__error-hint">
            <i className="pi pi-exclamation-circle" />
          </span>
        )}
      </div>

      {/* Right-side status dot for health values; lifecycle labels stay
          inside the subtitle so we don't duplicate them here. */}
      <div className="factor-list-item__badges">
        {isDirty && <DirtyIndicatorAtom variant="badge" />}
        {!isLifecycleStatus(status) && (
          <StatusBadgeAtom status={status as StatusValue} variant="dot" />
        )}
      </div>
    </div>
  );
};

/**
 * Get default factor name based on type
 */
function getDefaultFactorName(
  factorType: StatusFactorType | null,
  t: (key: string, defaultValue: string) => string
): string {
  switch (factorType) {
    case 'manual':
      return t('statusFactors.types.manual.label', 'Manual Check-In');
    case 'related_spheres':
      return t('statusFactors.types.relatedSpheres.label', 'Related Spheres');
    case 'arc_health':
      return t('statusFactors.types.arcHealth.label', 'Arc Health');
    default:
      return t('statusFactors.types.new.label', 'New Factor');
  }
}

/**
 * Get tooltip for factor type icon
 */
function getFactorTypeTooltip(
  factorType: StatusFactorType | null,
  t: (key: string, defaultValue: string) => string
): string {
  switch (factorType) {
    case 'manual':
      return t('statusFactors.types.manual.label', 'Manual Check-In');
    case 'related_spheres':
      return t('statusFactors.types.relatedSpheres.label', 'Related Spheres');
    case 'arc_health':
      return t('statusFactors.types.arcHealth.label', 'Arc Health');
    default:
      return t('statusFactors.types.unknown.label', 'Unknown Type');
  }
}

FactorListItemAtom.displayName = 'FactorListItemAtom';

export default FactorListItemAtom;
